import { Observable } from 'rxjs'
import { config } from '../../config/appConfig'
import { HttpService } from './http.service'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AuthPaths } from '../modules/authentication/authentication-routing.module'
import {
  IAppleUserInfo,
  IClientInfoResponse,
  IGenerateOTP,
  IGenerateOTPRequestBody,
  IGenerateOTPResponse,
  ILoginSuccessInfo,
  ITempToken,
  ITempTokenRequestBody,
  ITempTokenResponse,
  IVerifyOTPRequestBody,
  IVerifyOTPResponse,
  IVerifyRecaptchaResponse,
} from '../models/authentication'
import { Store } from '@ngrx/store'
import { HttpClient } from '@angular/common/http';
import { LoginSuccessFulAction, LogoutAction } from '../store/auth/auth.actions'
import { RefreshTokenAction } from '../store/user/user.actions'
import { selectRefreshToken } from '../store/auth/auth.reducer'
import { IResponse } from '../models/admin'
import { SessionService } from './session.service'

export enum AuthEndPoints {
  clientInfo = '/auth/clientInfo',
  otpVerify = '/auth/otp/verify',
  otpGenerate = '/auth/otp/generate',
  token = '/auth/token',
  recaptchaVerify = '/auth/recaptcha/verify'
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Inject Services
  private httpService;
  private router = inject(Router);
  private store = inject(Store);
  private sessionService = inject(SessionService);

  // TempTokenInfo
  private tempToken: string | null = null;
  private tempTokenExpiry: string | null = null;

  // Access Token Info
  private accessToken: string | null = null;
  private accessTokenExpiry: string | null = null;
  private refreshToken: string | null = null;
  private refreshScheduled = false;
  private refreshIntervalId: any = null; // Store interval ID to clear it later

  private OTPGenerateSuccessInfo: IGenerateOTP | null = null;

  private USER_SUB: string = 'google_user_sub';
  private TOKEN: string = 'token';
  private TOKEN_EXPIRY = 'token_expiry';
  private REFRESH_TOKEN = 'refresh_token';
  isLoggedIn: boolean = false;
  isParentalGateTokenRefresh: boolean = false;
  private isAutoLogin: boolean = false;

  refreshToken$: Observable<string | null>;

  constructor(private httpClient: HttpClient) {
    this.httpService = new HttpService(config.authServiceURL)
    this.refreshToken$ = this.store.select(selectRefreshToken);
  }

  setOTPGenerateSuccessInfo(info: IGenerateOTP) {
    this.OTPGenerateSuccessInfo = info;
  }

  getOTPGenerateSuccessInfo(): IGenerateOTP | null {
    return this.OTPGenerateSuccessInfo;
  }

  // Save the Temp Token Info
  saveTempTokenInfo(tokenInfo: ITempToken): void {
    const expiryTime = new Date().getTime() + tokenInfo.ExpiresIn * 1000;
    this.tempToken = tokenInfo.Token;
    this.tempTokenExpiry = expiryTime.toString();
  }

  // Get the Saved Temp Token INfo
  getSavedTempToken(): string | null {
    if (!this.tempTokenExpiry || Date.now() > parseInt(this.tempTokenExpiry, 10)) {
      return null;
    }
    return this.tempToken;
  }

  private calculateExpiresIn(expiryTime: string): number {
    const expiryTimestamp = parseInt(expiryTime, 10); // Convert expiry time string to number
    const currentTimestamp = new Date().getTime();
    const expiresInSeconds = (expiryTimestamp - currentTimestamp) / 1000;

    return expiresInSeconds > 0 ? Math.floor(expiresInSeconds) : 0;
  }

  async autoLogin(): Promise<boolean> {
    this.isParentalGateTokenRefresh = false;
    const accessTokenInfo = this.getLocalStorageTempTokenInfo();
    if (accessTokenInfo && new Date().getTime() < +accessTokenInfo.Expiry) {
      try {

        // Dispatch LoginSuccessFulAction after session initialization
        this.store.dispatch(LoginSuccessFulAction({
          loginSuccessInfo: {
            AccessToken: accessTokenInfo.Token,
            RefreshToken: accessTokenInfo.RefreshToken,
            Expiry: accessTokenInfo.Expiry
          }
        }));

        // Set token info and login state
        this.accessToken = accessTokenInfo.Token;
        this.accessTokenExpiry = accessTokenInfo.Expiry;
        this.refreshToken = accessTokenInfo.RefreshToken;
        this.scheduleTokenRefresh();
        // Initialize session before proceeding
        await this.sessionService.initializeApp();
        this.isLoggedIn = true;
        this.isAutoLogin = true;
        return true;
      } catch (error) {
        this.isAutoLogin = false;
        return false;
      }
    }
    this.isAutoLogin = true;
    return false;
  }

  isAutoLoggedIn(): boolean {
    return this.isAutoLogin && !this.isParentalGateTokenRefresh;
  }

  tokenRefreshedByParentalGate() {
    this.isParentalGateTokenRefresh = true;
  }

  private scheduleTokenRefresh() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId); // Clear any existing interval to avoid duplication
    }
    const expiresIn = this.calculateExpiresIn(this.accessTokenExpiry!);
    if (expiresIn > 60) {
      this.refreshIntervalId = setInterval(() => {
        this.store.dispatch(RefreshTokenAction({
          request: { Token: this.refreshToken!, RefreshParentalControl: true }
        }));

        // Subscribe to store to check if token is refreshed
        this.refreshToken$.subscribe(newToken => {
          if (!newToken) {
            this.store.dispatch(LogoutAction({})); // Logout if refresh fails
            clearInterval(this.refreshIntervalId); // Stop further refresh attempts
          }
        });
      }, (expiresIn - 30) * 1000);
    }
  }

  // Get the Decoded Token String (Returns null for any error)
  public getDecodedToken(tokenType: 'access' | 'temp' = 'access'): { [key: string]: any } | null {
    // Determine which token to decode
    const token = tokenType === 'access' ? this.accessToken : this.tempToken;

    // Check if token exists
    if (!token) {
      return null;
    }

    try {
      // Split the JWT into its parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode the payload (second part of JWT)
      const payload = parts[1];
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/')); // Handle base64url format
      const parsedPayload = JSON.parse(decodedPayload);

      return parsedPayload;
    } catch (error) {
      return null;
    }
  }

  public autoRedirect(redirectRoute: string = config.defaultRoute): void {
    this.router.navigate([redirectRoute])
  }

  public logout(): void {
    this.isLoggedIn = false;

    this.router.navigate([AuthPaths.signIn])

    // Remove Items From Local Storage
    localStorage.removeItem(this.TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
    localStorage.removeItem(this.TOKEN_EXPIRY);
  }

  public onSuccessFulLogin(accessTokenInfo: ILoginSuccessInfo) {
    // Update the References
    this.accessToken = accessTokenInfo.AccessToken;
    this.accessTokenExpiry = accessTokenInfo.Expiry;
    this.refreshToken = accessTokenInfo.RefreshToken;

    // Store to Local Storage
    localStorage.setItem(this.TOKEN, accessTokenInfo.AccessToken);
    localStorage.setItem(this.TOKEN_EXPIRY, accessTokenInfo.Expiry);
    localStorage.setItem(this.REFRESH_TOKEN, accessTokenInfo.RefreshToken);

    if (!this.refreshScheduled) {
      this.scheduleTokenRefresh();
      this.refreshScheduled = true;
    }

    this.isLoggedIn = true;
  }

  public getTokenInformation(): { AccessToken: string, Expiry: string, RefreshToken: string } | null {
    if (!this.accessToken || !this.accessTokenExpiry || !this.refreshToken) {
      return null;
    }
    return {
      AccessToken: this.accessToken,
      Expiry: this.accessTokenExpiry,
      RefreshToken: this.refreshToken
    }
  }

  private getLocalStorageTempTokenInfo(): {
    Token: string,
    Expiry: string,
    RefreshToken: string
  } | null {
    const accessToken = localStorage.getItem(this.TOKEN);
    const accessTokenExpiry = localStorage.getItem(this.TOKEN_EXPIRY);
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN);

    if (!accessToken || !accessTokenExpiry || !refreshToken) {
      return null;
    }

    return {
      Token: accessToken,
      Expiry: accessTokenExpiry,
      RefreshToken: refreshToken
    }
  }

  public getUserSub(): string | null {
    return localStorage.getItem(this.USER_SUB)
  }


  // Repository Services

  // Fetch Client Info
  public getClientInfo(): Observable<IClientInfoResponse> {
    return this.httpService.get(AuthEndPoints.clientInfo)
  }

  // Fetch Temp Token
  public getTempToken(
    authCode: string,
    redirectUri: string,
    provider: 'apple' | 'google',
    appleUser?: IAppleUserInfo | null
  ): Observable<ITempTokenResponse> {
    const reqBody: ITempTokenRequestBody = {
      AuthCode: authCode,
      RedirectUri: redirectUri,
      AppleUser: appleUser ?? null
    }

    const clientType = provider === "apple" ? config.iosClientType : config.clientType;

    return this.httpService.post(AuthEndPoints.token, reqBody, {
      'X-Client-TYPE': clientType
    });
  }

  // request for OTP
  public requestForOTP(otpInfo: IGenerateOTPRequestBody): Observable<IGenerateOTPResponse> {
    return this.httpService.post(AuthEndPoints.otpGenerate, otpInfo)
  }

  public verifyOtp(otpInfo: IVerifyOTPRequestBody): Observable<IVerifyOTPResponse> {
    return this.httpService.post(AuthEndPoints.otpVerify, otpInfo)
  }

  public verifyRecaptcha(recaptchaUserToken: string): Observable<IResponse<IVerifyRecaptchaResponse>> {
    return this.httpService.post(AuthEndPoints.recaptchaVerify, {
      RecaptchaUserToken: recaptchaUserToken
    });
  }
}
